import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import type {
  AttachmentDto,
  AttachmentId,
  Currency,
  InvoiceEntity,
  InvoiceMatchSnapshot,
  NewInvoiceEntity,
} from "../../api/types";
import {
  useAttachments,
  useDeleteAttachment,
  useReplaceAttachmentContent,
  useUpdateAttachment,
} from "../../hooks/useAttachments";
import { makeDateString, CurrencySchema } from "../../api/types";
import { ApiError, errorText } from "../../api/common";
import { makeMoney } from "../../logic/money";
import { useInvoiceDetectors } from "../../api/lists";
import { getAttachmentBlob } from "../../api/attachments";
import { extractPdfText } from "../../utils/pdfText";
import { renderPdfFirstPageToWebp } from "../../utils/pdfToWebp";
import {
  checkReplacement,
  detectInvoice,
  detectUnsupportedInvoice,
  hasReadableText,
  type DetectionResult,
} from "../../logic/invoice-detection";
import type { AutofillApply } from "../../logic/invoice-autofill-diff";
import { useRegistry } from "../../context/RegistryContext";
import {
  draftSenderFields,
  draftSenderId,
  draftWithSender,
  invoiceSender,
  senderDraftFor,
  type SenderDraft,
} from "../../logic/invoice-sender";
import { InvoiceAutofillReviewDialog } from "./InvoiceAutofillReviewDialog";
import { InvoiceDuplicateDialog } from "./InvoiceDuplicateDialog";
import { AttachmentSelectModal } from "../AttachmentSelectModal";
import AttachmentPreview from "../AttachmentPreview";
import { AttachmentPreviewInline } from "../AttachmentPreviewInline";
import { AttachmentUploadModal } from "../AttachmentUploadModal";
import { DatePicker } from "../DatePicker";
import { InvoiceSenderField } from "./InvoiceSenderField";

export interface InvoiceFormDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Persist the entity; reject to keep the form open with the error shown.
   * An `ApiError` of type `invoiceDuplicate` opens the duplicate dialog
   * instead, whose "Create anyway" calls this again with `force: true`.
   */
  onSave: (
    entity: NewInvoiceEntity,
    options: { force: boolean },
  ) => Promise<void> | void;
  initialData?: InvoiceEntity | null;
  prefilledAttachmentId?: AttachmentId;
  /** Run PDF detection automatically once the form opens (dashboard shortcut). */
  autoDetectOnOpen?: boolean;
  title: string;
}

export const InvoiceFormDialog = ({
  open,
  onClose,
  onSave,
  initialData,
  prefilledAttachmentId,
  autoDetectOnOpen,
  title,
}: InvoiceFormDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isWide = useMediaQuery(theme.breakpoints.up("md"));

  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || "",
  );
  const [sender, setSender] = useState<SenderDraft>(() =>
    senderDraftFor(initialData),
  );
  const [dueDate, setDueDate] = useState(initialData?.dueDate || "");
  const [dateReceived, setDateReceived] = useState(
    initialData?.dateReceived || "",
  );
  const [amount, setAmount] = useState(initialData?.amount?.amount ?? "");
  const [amountMax, setAmountMax] = useState(
    initialData?.amountMax?.amount ?? "",
  );
  const [currency, setCurrency] = useState<Currency>(
    initialData?.amount?.currency ?? initialData?.amountMax?.currency ?? "NOK",
  );
  const [kid, setKid] = useState(initialData?.kid ?? "");
  const [payToAccount, setPayToAccount] = useState(
    initialData?.payToAccount ?? "",
  );
  const [attachments, setAttachments] = useState<AttachmentId[]>(
    initialData?.attachments ||
      (prefilledAttachmentId ? [prefilledAttachmentId] : []),
  );
  const [activePreviewId, setActivePreviewId] = useState<
    AttachmentId | undefined
  >(attachments[0]);

  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Null until the user or auto-fill overrides it, so the field defaults to the
  // target attachment's current name without a `useEffect` sync.
  const [filenameOverride, setFilenameOverride] = useState<string | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [detectMessage, setDetectMessage] = useState<{
    severity: "success" | "warning" | "error";
    text: string;
  } | null>(null);
  // Set while the review dialog is open with a fresh detection to confirm.
  const [pendingDetection, setPendingDetection] =
    useState<DetectionResult | null>(null);
  // Set when the attached PDF can't be auto-filled but swapping it for the
  // original would fix that. `detectorId` is the detector a replacement must
  // match — known when a nested unreadable variant named the vendor, and null
  // for a picture with no text layer, which names nobody (see
  // `checkReplacement`). The whole thing is null when no swap is on offer.
  const [replaceTarget, setReplaceTarget] = useState<{
    detectorId: string | null;
  } | null>(null);
  const [replacing, setReplacing] = useState(false);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  // Detector output captured for the server to generate this invoice's
  // read-only virtual auto-transaction rule. Kept independent of the field
  // review so it survives to submit.
  const [matchSnapshot, setMatchSnapshot] =
    useState<InvoiceMatchSnapshot | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // The existing invoice the server matched this one to; set while the
  // duplicate dialog is up.
  const [duplicate, setDuplicate] = useState<InvoiceEntity | null>(null);

  const { data: availableAttachments } = useAttachments();
  const { data: detectorConfig } = useInvoiceDetectors();
  const { withHidden } = useRegistry();
  const updateAttachment = useUpdateAttachment();
  const replaceContent = useReplaceAttachmentContent();
  const deleteAttachment = useDeleteAttachment();

  // The PDF the auto-fill / filename field act on: the previewed attachment if
  // it is a PDF, otherwise the first PDF among the selected attachments.
  const isPdf = (a: AttachmentDto | undefined): boolean =>
    a?.contentType === "application/pdf";
  const activeMeta = availableAttachments?.find(
    (a) => a.id === activePreviewId,
  );
  const targetPdf: AttachmentDto | undefined =
    (isPdf(activeMeta) ? activeMeta : undefined) ??
    availableAttachments?.find((a) => attachments.includes(a.id) && isPdf(a));
  const currentFilename = targetPdf?.filename ?? "";
  const filename = filenameOverride ?? currentFilename;

  const handleAutoFill = useCallback(async () => {
    if (!targetPdf) return;
    setDetecting(true);
    setDetectMessage(null);
    setReplaceTarget(null);
    try {
      const blob = await getAttachmentBlob(targetPdf);
      const text = await extractPdfText(blob);
      // No text layer (a scan, a screenshot, an image-exported invoice) — there's
      // nothing for detectors to match. The fix is the same one a scrambled copy
      // gets: swap in the original download. Nothing here names the vendor, so
      // the offer carries no detector and the replacement is validated only by
      // being an invoice we can read.
      if (!hasReadableText(text)) {
        setReplaceTarget({ detectorId: null });
        setDetectMessage({
          severity: "warning",
          text: "This PDF has no readable text — it's a picture of the invoice (a scan, a screenshot, or an app's “share” export) rather than the real download. Auto-fill reads text, not images. Attach the original PDF from the issuer or your bank and it will fill in by itself; otherwise enter the fields by hand.",
        });
        return;
      }
      const result = detectInvoice(text, detectorConfig?.detectors ?? []);
      if (!result) {
        // Recognised-but-unsupported invoices (e.g. scrambled-font PDFs) get a
        // specific, actionable message instead of the generic fallback.
        const unsupported = detectUnsupportedInvoice(
          text,
          detectorConfig?.detectors ?? [],
          detectorConfig?.unsupported ?? [],
        );
        // Only an unreadable copy nested under a detector can be fixed by
        // swapping the file: that detector is what validates the replacement.
        setReplaceTarget(
          unsupported?.replacementDetectorId != null
            ? { detectorId: unsupported.replacementDetectorId }
            : null,
        );
        setDetectMessage({
          severity: "warning",
          text:
            unsupported?.reason ??
            "Couldn't recognize this invoice. Add it to the detectors to support it.",
        });
        return;
      }
      // Don't touch the form yet — let the user review/edit the changes first.
      // KID and pay-to-account are proposed as ordinary reviewable fields (see
      // the review dialog), so they flow into the form through `applyAutofill`,
      // not through the snapshot.
      setPendingDetection(result);
      // Capture the detector snapshot (detector id + categorization) so the
      // server can build this invoice's virtual matching rule. The rule reads
      // the categorization live off the detector by id; this copy is only its
      // fallback should the detector leave the config. Requires a
      // transactionInfo; a detector without one produces no rule.
      const info = result.suggestion.transactionInfo;
      setMatchSnapshot(
        info ? { detectorId: result.detectorId, transactionInfo: info } : null,
      );
    } catch (err) {
      // Surface the underlying reason: iOS Safari has no visible console, so the
      // message has to reach the user through the banner to be diagnosable.
      console.error("Invoice auto-fill failed", err);
      setDetectMessage({
        severity: "error",
        text: `Failed to read the PDF: ${
          err instanceof Error ? err.message : String(err)
        }`,
      });
    } finally {
      setDetecting(false);
    }
  }, [targetPdf, detectorConfig]);

  /**
   * Validate a file offered in place of an unreadable copy, and only swap the
   * stored attachment when the detector that owns the unreadable variant claims
   * it. Anything else leaves the stored file exactly as it was.
   */
  const handleReplacePicked = useCallback(
    async (file: File) => {
      if (!targetPdf || !replaceTarget) return;
      setReplacing(true);
      setDetectMessage(null);
      try {
        const detectors = detectorConfig?.detectors ?? [];
        const { detectorId: requiredId } = replaceTarget;
        const check = checkReplacement(
          await extractPdfText(file),
          detectors,
          detectorConfig?.unsupported ?? [],
          requiredId,
        );
        if (check.status !== "ok") {
          const label =
            (requiredId != null
              ? detectors.find((d) => d.id === requiredId)?.label
              : null) ?? "this invoice";
          setDetectMessage({
            severity: "warning",
            text:
              check.status === "noTextLayer"
                ? "That PDF has no readable text either — it looks like a scan or a picture. Download the original file rather than sharing or printing it to PDF. Nothing was changed."
                : check.status === "stillUnreadable"
                  ? "That copy has the same scrambled text as the one already attached. Download the original from the issuer rather than re-saving or sharing it. Nothing was changed."
                  : requiredId == null
                    ? "That PDF reads fine, but nothing recognises it as an invoice we support, so it can't be filled in automatically. Nothing was changed."
                    : check.detectorId
                      ? `That PDF reads fine, but it's a different invoice — it looks like ${check.detectorLabel ?? check.detectorId}, not ${label}. Nothing was changed.`
                      : `That PDF reads fine, but nothing recognises it as ${label}. Nothing was changed.`,
          });
          return;
        }

        // Regenerate the preview from the new file: the old one depicts the
        // copy being replaced. If rendering fails the swap still goes ahead —
        // the readable file is the point — but the backend drops the stale
        // preview, so say so rather than leaving a silently previewless PDF.
        let previewFailed = false;
        const preview = await renderPdfFirstPageToWebp(file).catch((e) => {
          console.error("Preview render failed during replacement", e);
          previewFailed = true;
          return undefined;
        });
        // Only the content changes here, under the name the attachment already
        // has. The detector's filename reaches the form through the review
        // below, like any auto-fill, and is applied by the post-save rename —
        // so a cancel or a duplicate refusal leaves the attachment's name as
        // it was, exactly as on the ordinary auto-fill path.
        await replaceContent.mutateAsync({
          id: targetPdf.id,
          file,
          filename: targetPdf.filename,
          contentType: "application/pdf",
          preview,
        });
        if (previewFailed) {
          setDetectMessage({
            severity: "warning",
            text: "The PDF was replaced, but its preview couldn't be rendered. Open the attachment and use “Generate preview” to make one.",
          });
        }

        // Same id, so nothing that references this attachment has to change —
        // carry straight on into the ordinary review of what was detected.
        setReplaceTarget(null);
        setPendingDetection(check.result);
        const info = check.result.suggestion.transactionInfo;
        setMatchSnapshot(
          info
            ? { detectorId: check.result.detectorId, transactionInfo: info }
            : null,
        );
      } catch (err) {
        console.error("Invoice PDF replacement failed", err);
        setDetectMessage({
          severity: "error",
          text: `Failed to replace the PDF: ${
            err instanceof Error ? err.message : String(err)
          }`,
        });
      } finally {
        setReplacing(false);
      }
    },
    [targetPdf, replaceTarget, detectorConfig, replaceContent],
  );

  // The form's sender as the review diffs it: only the chosen kind counts.
  // Named, not offered: an invoice already sent by a hidden payee still shows
  // who it is in the review.
  const currentSender = invoiceSender(draftSenderFields(sender), withHidden);

  const applyAutofill = (applied: AutofillApply) => {
    if (applied.name !== undefined) setName(applied.name);
    const { payeeId, sourceId } = applied;
    if (payeeId !== undefined)
      setSender((prev) =>
        draftWithSender(prev, { kind: "payee", id: payeeId }),
      );
    if (sourceId !== undefined)
      setSender((prev) =>
        draftWithSender(prev, { kind: "source", id: sourceId }),
      );
    if (applied.amount !== undefined) setAmount(applied.amount);
    if (applied.amountMax !== undefined) setAmountMax(applied.amountMax);
    if (applied.currency !== undefined) setCurrency(applied.currency);
    if (applied.kid !== undefined) setKid(applied.kid);
    if (applied.payToAccount !== undefined)
      setPayToAccount(applied.payToAccount);
    if (applied.dateReceived !== undefined)
      setDateReceived(applied.dateReceived);
    if (applied.dueDate !== undefined) setDueDate(applied.dueDate);
    if (applied.filename !== undefined) setFilenameOverride(applied.filename);
    setPendingDetection(null);
  };

  // Dashboard "invoice from PDF" shortcut: once the uploaded PDF and the
  // detectors are ready, run detection once so the review dialog pops on its
  // own. Guarded by a ref so it fires a single time per open.
  const autoDetectStarted = useRef(false);
  useEffect(() => {
    if (
      autoDetectOnOpen &&
      !autoDetectStarted.current &&
      detectorConfig &&
      targetPdf
    ) {
      autoDetectStarted.current = true;
      void handleAutoFill();
    }
  }, [autoDetectOnOpen, detectorConfig, targetPdf, handleAutoFill]);

  // The rename the filename field (typed, or applied from auto-fill) asks for,
  // or null when it leaves the PDF's name alone.
  const trimmedFilename = filename.trim();
  const pendingRename =
    targetPdf && trimmedFilename && trimmedFilename !== targetPdf.filename
      ? {
          id: targetPdf.id,
          body: { filename: trimmedFilename, notes: targetPdf.notes ?? null },
        }
      : null;

  const save = async (force: boolean) => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(
        {
          name,
          description: description || null,
          ...draftSenderFields(sender),
          dueDate: makeDateString(dueDate),
          dateReceived: makeDateString(dateReceived),
          amount: amount.trim() ? makeMoney(amount, currency) : null,
          amountMax: amountMax.trim() ? makeMoney(amountMax, currency) : null,
          kid: kid.trim() || null,
          payToAccount: payToAccount.trim() || null,
          attachments,
          matchSnapshot: matchSnapshot ?? undefined,
        },
        { force },
      );
      // The rename is part of saving the invoice, so it waits for the save to
      // land. Until then nothing is committed: a duplicate answered with "Open
      // existing", a cancel, or a failed save all leave the upload exactly as
      // it came in — its own name, still pending — rather than already wearing
      // the existing invoice's filename.
      if (pendingRename) updateAttachment.mutate(pendingRename);
    } catch (err) {
      if (err instanceof ApiError && err.data.type === "invoiceDuplicate") {
        setDuplicate(err.data.existingInvoice);
      } else {
        setSaveError(errorText(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    void save(false);
  };

  // "Discard upload" is only offered for the attachment this form was opened
  // from, and only while nothing else references it: the duplicate means the
  // upload itself was the mistake, but a file another invoice or transaction
  // already links is theirs, not this form's, to delete. The server refuses a
  // referenced attachment as well, so this is a courtesy, not the guard.
  const uploadedAttachment = prefilledAttachmentId
    ? availableAttachments?.find((a) => a.id === prefilledAttachmentId)
    : undefined;
  const canDiscardUpload =
    uploadedAttachment !== undefined &&
    !uploadedAttachment.hasInvoice &&
    !uploadedAttachment.hasTransaction;

  const handleDiscardUpload = async () => {
    if (!prefilledAttachmentId) return;
    setSaving(true);
    try {
      await deleteAttachment.mutateAsync(prefilledAttachmentId);
      onClose();
    } catch (err) {
      // Back to the form with the reason; the upload is still there.
      setDuplicate(null);
      setSaveError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const activeAttachmentMeta = activePreviewId
    ? availableAttachments?.find((a) => a.id === activePreviewId)
    : undefined;

  const previewPane = availableAttachments && activeAttachmentMeta && (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}
    >
      <AttachmentPreviewInline attachment={activeAttachmentMeta} />
      {attachments.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ overflowX: "auto" }}>
          {attachments.map((aid) => {
            const meta = availableAttachments.find((a) => a.id === aid);
            if (!meta) return null;
            return (
              <Box
                key={aid}
                onClick={() => setActivePreviewId(aid)}
                sx={{
                  border: (t) =>
                    `2px solid ${
                      aid === activePreviewId
                        ? t.palette.primary.main
                        : "transparent"
                    }`,
                  borderRadius: 1,
                  p: 0.5,
                }}
              >
                <AttachmentPreview attachment={meta} />
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );

  const attachmentSelector = availableAttachments && (
    <>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={() => setIsAttachmentModalOpen(true)}
          fullWidth
          sx={{ justifyContent: "flex-start", py: 1.5 }}
        >
          Select Attachments ({attachments.length} selected)
        </Button>
        <Button
          variant="outlined"
          onClick={() => setIsUploadModalOpen(true)}
          // Without flexShrink the fullWidth sibling squeezes this below its
          // own text width, and `nowrap` then clips the label.
          sx={{ whiteSpace: "nowrap", py: 1.5, flexShrink: 0 }}
        >
          Upload new
        </Button>
      </Stack>
      {!isWide && attachments.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          {attachments.map((aid) => {
            const meta = availableAttachments.find((a) => a.id === aid);
            if (!meta) return null;
            return <AttachmentPreview key={aid} attachment={meta} />;
          })}
        </Stack>
      )}
      <AttachmentSelectModal
        open={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
        onSave={(selectedIds) => {
          const typed = selectedIds as AttachmentId[];
          setAttachments(typed);
          if (!typed.includes(activePreviewId as AttachmentId)) {
            setActivePreviewId(typed[0]);
          }
          setIsAttachmentModalOpen(false);
        }}
        availableAttachments={availableAttachments}
        initialSelectedIds={attachments}
      />
      <AttachmentUploadModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadSuccess={(newAttachment) => {
          const newId = newAttachment.id as AttachmentId;
          setAttachments((prev) =>
            prev.includes(newId) ? prev : [...prev, newId],
          );
          setActivePreviewId(newId);
          setIsUploadModalOpen(false);
        }}
      />
    </>
  );

  const formFields = (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {saveError && (
        <Alert severity="error" onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      {targetPdf && (
        <Button
          variant="outlined"
          onClick={handleAutoFill}
          disabled={detecting}
          startIcon={
            detecting ? <CircularProgress size={16} /> : <AutoFixHighIcon />
          }
        >
          {detecting ? "Reading PDF…" : "Auto-fill from PDF"}
        </Button>
      )}
      {detectMessage && (
        <Alert
          severity={detectMessage.severity}
          onClose={() => setDetectMessage(null)}
          action={
            replaceTarget ? (
              <Button
                color="inherit"
                size="small"
                disabled={replacing}
                onClick={() => replaceInputRef.current?.click()}
              >
                {replacing ? "Checking…" : "Replace with original"}
              </Button>
            ) : undefined
          }
        >
          {detectMessage.text}
        </Alert>
      )}
      {/* Kept out of the Alert so dismissing the banner mid-check can't unmount
          the input while the picker is open. */}
      <Box
        component="input"
        type="file"
        accept="application/pdf"
        ref={replaceInputRef}
        sx={{ display: "none" }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          // Reset first: picking the same file twice must still fire onChange.
          e.target.value = "";
          if (file) void handleReplacePicked(file);
        }}
      />

      <TextField
        required
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        slotProps={{ htmlInput: { maxLength: 50 } }}
      />

      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        rows={2}
      />

      <InvoiceSenderField value={sender} onChange={setSender} />

      {/* Three controls in one row leaves ~110px each on a phone, which
          truncates the "Max amount" label — stack them below sm instead. */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField
          label="Amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          fullWidth
          slotProps={{ htmlInput: { step: "0.01" } }}
          helperText="Optional"
        />
        <TextField
          label="Max amount"
          type="number"
          value={amountMax}
          onChange={(e) => setAmountMax(e.target.value)}
          fullWidth
          slotProps={{ htmlInput: { step: "0.01" } }}
          helperText="Optional — matches a range"
        />
        <FormControl sx={{ minWidth: 110 }}>
          <InputLabel id="invoice-currency-label">Currency</InputLabel>
          <Select
            labelId="invoice-currency-label"
            value={currency}
            label="Currency"
            onChange={(e) => setCurrency(e.target.value as Currency)}
          >
            {CurrencySchema.options.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack direction="row" spacing={1}>
        <TextField
          label="KID"
          value={kid}
          onChange={(e) => setKid(e.target.value)}
          fullWidth
          helperText="Optional — payment reference"
        />
        <TextField
          label="Pay-to account"
          value={payToAccount}
          onChange={(e) => setPayToAccount(e.target.value)}
          fullWidth
          helperText="Optional — creditor account"
        />
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          Date Received
        </Typography>
        <DatePicker
          mode="single"
          label="Date Received"
          required
          fullWidth
          value={dateReceived || null}
          onChange={(v) => setDateReceived(v ?? "")}
        />
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          Due Date
        </Typography>
        <DatePicker
          mode="single"
          label="Due Date"
          required
          fullWidth
          value={dueDate || null}
          onChange={(v) => setDueDate(v ?? "")}
        />
      </Stack>

      {targetPdf && (
        <TextField
          label="Filename"
          value={filename}
          onChange={(e) => setFilenameOverride(e.target.value)}
          fullWidth
          helperText="Renames the attached PDF when you save"
          slotProps={{ htmlInput: { maxLength: 255 } }}
        />
      )}

      {attachmentSelector}
    </Stack>
  );

  const splitView = isWide && attachments.length > 0;

  return (
    <>
      {pendingDetection && (
        <InvoiceAutofillReviewDialog
          open
          onClose={() => setPendingDetection(null)}
          result={pendingDetection}
          attachment={targetPdf}
          current={{
            name,
            sender: currentSender?.entity
              ? { kind: currentSender.kind, name: currentSender.entity.name }
              : null,
            amount,
            amountMax,
            currency,
            kid,
            payToAccount,
            dateReceived,
            dueDate,
            filename: currentFilename,
          }}
          onApply={applyAutofill}
        />
      )}
      {duplicate && (
        <InvoiceDuplicateDialog
          existing={duplicate}
          canDiscardUpload={canDiscardUpload}
          busy={saving}
          onOpenExisting={onClose}
          onDiscardUpload={() => void handleDiscardUpload()}
          onCreateAnyway={() => {
            setDuplicate(null);
            void save(true);
          }}
          onCancel={() => setDuplicate(null)}
        />
      )}
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth={splitView ? "lg" : "sm"}
        fullWidth
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent dividers>
            {splitView ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  alignItems: "stretch",
                  minHeight: 500,
                }}
              >
                <Box sx={{ minHeight: 0 }}>
                  {previewPane || (
                    <Typography
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      No attachment selected
                    </Typography>
                  )}
                </Box>
                <Box>{formFields}</Box>
              </Box>
            ) : (
              formFields
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                saving ||
                !name ||
                !draftSenderId(sender) ||
                !dueDate ||
                !dateReceived
              }
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
